import { type FC, type ReactNode, useMemo, useState } from "react";

import {
  Box,
  Checkbox,
  Column,
  Combobox,
  InformationIcon,
  NumberInput,
  Row,
  Select,
  BadgeInput,
  Text,
  TextInput,
  Tooltip,
  useToast,
} from "@hightouch/htui";
import { uniqBy } from "lodash";
import Papa from "papaparse";
import pluralize from "pluralize";
import { HiddenFormLabel } from "src/components/form/hidden-form-label";
import Dropzone, { type FileRejection } from "react-dropzone";

import { useFilterableOptionsContext } from "src/components/explore/filter-popover/filterable-options-hook";
import {
  BooleanOperator,
  ColumnDateTypes,
  type ColumnReference,
  ColumnType,
  type FilterableColumn,
  MultiValueColumnTypes,
  MultiValueOperators,
  NumberOperator,
  OperatorsWithoutValue,
  PercentileOperators,
  type PropertyCondition,
  isRawColumn,
  isRelatedColumn,
  isTraitColumn,
  isTransformedColumn,
} from "src/types/visual";
import { abbreviateNumber } from "src/utils/numbers";

import { ErrorMessage } from "./error-message";
import { NumberRangeInput } from "./number-range-input";
import { TimestampInput } from "./timestamp-input";
import type { MinimalParentModel } from "src/components/audiences/types";
import { getPropertyType } from "@hightouch/lib/query/visual/types";
import type { UpdaterEnforceDefined } from "src/utils/immutable-helpers";
import { captureMessage } from "src/lib/monitoring";

export type PropertyInputProps = {
  condition: PropertyCondition;
  isDisabled?: boolean;
  onUpdate: (updater: UpdaterEnforceDefined<PropertyCondition>) => void;
  onSearchUpdate?: (value: string) => void;
  loading?: boolean;
  suggestions:
    | Array<{
        value: string | number | boolean | null;
        count: number | undefined;
        label?: string;
      }>
    | undefined
    | null;
  error?: ReactNode;
  columns?: FilterableColumn[] | undefined;
  parent?: MinimalParentModel | undefined;
  allowMultiValue?: boolean;
  allowPercentile?: boolean;
  /** Only for legacy timestamp property comparison. Most property comparison is handled outside of PropertyInput */
  allowPropertyComparison: boolean;
  includeParentInPropertyComparison?: boolean;
  removePortal?: boolean;
};

export const PropertyInput: FC<Readonly<PropertyInputProps>> = ({
  condition,
  error,
  loading,
  suggestions,
  onUpdate,
  onSearchUpdate,
  columns,
  parent,
  isDisabled = false,
  allowMultiValue = true,
  allowPercentile = true,
  allowPropertyComparison,
  includeParentInPropertyComparison,
  removePortal = true,
}) => {
  const { toast } = useToast();

  // Just need a model name for the tooltip below. The previous version
  // called the heavy `useConditionSelect` hook (which builds full
  // property/trait/event/relation/audience/journey option groups, runs an
  // AI suggestions query, and runs effects) for every PropertyInput
  // instance — duplicated across every condition row. Walk the condition's
  // column reference and look up the name directly from the
  // already-mounted FilterableOptionsContext.
  const { columns: contextColumns, traits } = useFilterableOptionsContext();
  const modelName = useMemo(
    () => resolveModelName(condition.property, contextColumns, traits),
    [condition.property, contextColumns, traits],
  );

  const propertyType = getPropertyType(condition);

  const suggestionOptions = useMemo(
    () =>
      suggestions?.map(({ value, count, label }) => ({
        label: label ?? String(value),
        description: count ? `(${abbreviateNumber(count)})` : undefined,
        value,
      })),
    [suggestions],
  );

  const [createdOptions, setCreatedOptions] = useState<
    Array<{ label: string; value: string; description?: string }>
  >([]);

  const multiSelectOptions = useMemo(
    () =>
      uniqBy(
        [
          ...createdOptions,
          ...(suggestionOptions || []),
          ...(condition.value
            ? (Array.isArray(condition.value)
                ? condition.value
                : [condition.value]
              ).map((value) => ({ label: value, value }))
            : []),
        ],
        "value",
      ),
    [createdOptions, suggestionOptions, condition.value],
  );

  const multiSelectSuggestionsTip = useMemo(() => {
    return `${
      multiSelectOptions.length
        ? `${multiSelectOptions.length} ${pluralize(
            "value",
            multiSelectOptions.length,
          )} found in ${modelName ?? "model"}, ordered by prevalence. `
        : ""
    }Type to create a new value.`;
  }, [multiSelectOptions.length, modelName]);

  const suggestionsTip = useMemo(() => {
    return `${
      suggestionOptions?.length
        ? `${suggestionOptions.length} ${pluralize(
            "value",
            suggestionOptions.length,
          )} found in ${modelName ?? "model"}, ordered by prevalence. `
        : ""
    }Type to create a new value.`;
  }, [suggestionOptions?.length, modelName]);

  if (OperatorsWithoutValue.includes(condition.operator)) {
    return null;
  }

  if (
    MultiValueOperators.includes(condition.operator) &&
    propertyType &&
    MultiValueColumnTypes.includes(propertyType) &&
    allowMultiValue
  ) {
    const handleFileDrop = (
      acceptedFiles: File[],
      fileRejections: FileRejection[],
    ) => {
      if (fileRejections.length > 0) {
        toast({
          id: "file-upload",
          title:
            'Each file must be in ".csv" or ".txt" format and uploaded one at a time',
          variant: "error",
        });
      }

      if (acceptedFiles.length === 1) {
        const file = acceptedFiles[0];

        if (file) {
          Papa.parse(file, {
            complete: (results) => {
              const values = results.data
                .flat()
                .filter(Boolean)
                .map((value) => (value as string).trim());

              toast({
                id: "file-upload",
                title: `Values from '${file.name}' were added`,
                variant: "success",
              });

              if (values.length > 500) {
                captureMessage(
                  `Attempting to load more than 500 values via file in property input`,
                  { level: "warning", extra: { amount: values.length } },
                );
              }

              setCreatedOptions([
                ...createdOptions,
                ...values.map((value) => ({ label: value, value })),
              ]);
              onUpdate((condition) => {
                if (!Array.isArray(condition.value)) {
                  condition.value = [];
                }
                condition.value.push(...values);
              });
            },
          });
        }
      }
    };

    return (
      <Dropzone
        accept={[".csv", ".txt"]}
        maxFiles={1}
        multiple={false}
        noClick={true}
        noKeyboard={true}
        onDrop={handleFileDrop}
      >
        {({ getRootProps, getInputProps, isDragActive }) => {
          return (
            <Column>
              <Row align="start" gap={2} {...getRootProps()}>
                <BadgeInput
                  removePortal={removePortal}
                  {...getInputProps()}
                  supportsCreatableOptions
                  isDisabled={isDisabled}
                  size="md"
                  width="md"
                  isLoading={loading}
                  isInvalid={Boolean(error)}
                  options={multiSelectOptions}
                  placeholder={
                    isDragActive
                      ? "Upload your file here..."
                      : "Add values or drag/drop a text file..."
                  }
                  value={
                    !Array.isArray(condition.value) && condition.value
                      ? [condition.value]
                      : (condition.value ?? [])
                  }
                  onChange={(options) => {
                    const values = options.map((option) =>
                      typeof option === "string" ? option.trim() : option,
                    );
                    onUpdate((condition) => {
                      condition.value = values;
                    });
                  }}
                  onCreateOption={(createdValue) => {
                    const newValue = Array.isArray(condition.value)
                      ? condition.value
                      : condition.value === null
                        ? []
                        : [condition.value];

                    setCreatedOptions([
                      ...createdOptions,
                      {
                        label: createdValue,
                        value: createdValue,
                      },
                    ]);
                    onUpdate((condition) => {
                      condition.value = [...newValue, createdValue];
                    });
                  }}
                  onSearchUpdate={onSearchUpdate}
                />

                <Tooltip
                  message={multiSelectSuggestionsTip}
                  placement="top-end"
                >
                  <Text size="lg" mt={1}>
                    <InformationIcon />
                  </Text>
                </Tooltip>
              </Row>

              {error && <ErrorMessage>{error}</ErrorMessage>}
            </Column>
          );
        }}
      </Dropzone>
    );
  }

  if (
    typeof propertyType === "string" &&
    [ColumnType.Number, ColumnType.BigInt].includes(propertyType) &&
    condition.operator === NumberOperator.Between
  ) {
    return (
      <NumberRangeInput
        isDisabled={isDisabled}
        condition={condition}
        error={error}
        suggestionsLoading={loading}
        suggestionOptions={suggestionOptions ?? []}
        suggestionsTip={suggestionsTip}
        onUpdate={onUpdate}
        bigint={propertyType === ColumnType.BigInt}
      />
    );
  }

  if (
    propertyType &&
    !ColumnDateTypes.includes(propertyType) &&
    suggestions?.length
  ) {
    return (
      <Column>
        <Row gap={2} align="start">
          <Combobox
            removePortal={removePortal}
            isClearable
            supportsCreatableOptions
            isLoading={loading}
            isDisabled={isDisabled}
            isInvalid={Boolean(error)}
            optionLabel={(option) => option.label}
            options={suggestionOptions ?? []}
            placeholder="search..."
            value={condition.value}
            onChange={(value) =>
              onUpdate((condition) => {
                condition.value = value;
              })
            }
            onCreateOption={(value) =>
              onUpdate((condition) => {
                condition.value = value;
              })
            }
          />

          <Tooltip message={suggestionsTip}>
            <Text as={Column} justifyContent="center" size="lg" height="100%">
              <InformationIcon />
            </Text>
          </Tooltip>
        </Row>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </Column>
    );
  }

  if (propertyType === ColumnType.Boolean) {
    return (
      <Column>
        <Select
          removePortal={removePortal}
          isDisabled={isDisabled}
          isInvalid={Boolean(error)}
          options={[
            {
              value: true,
              label: "True",
            },
            {
              value: false,
              label: "False",
            },
          ]}
          placeholder="True / False"
          value={condition.value}
          width="auto"
          onChange={(value) => {
            onUpdate((condition) => {
              condition.operator = BooleanOperator.Equals;
              condition.value = value;
            });
          }}
        />
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </Column>
    );
  }

  if (propertyType && ColumnDateTypes.includes(propertyType)) {
    return (
      <TimestampInput
        isDisabled={isDisabled}
        condition={condition}
        error={error}
        hideTime={propertyType === ColumnType.Date}
        onUpdate={onUpdate as any}
        columns={columns}
        parent={parent}
        removePortal={removePortal}
        allowPropertyComparison={allowPropertyComparison}
        includeParentInPropertyComparison={includeParentInPropertyComparison}
      />
    );
  }

  return (
    <Row gap={2}>
      <Column>
        {propertyType &&
        [ColumnType.Number, ColumnType.JsonArrayNumbers].includes(
          propertyType,
        ) ? (
          <HiddenFormLabel label="Enter a number">
            <NumberInput
              isDisabled={isDisabled}
              isInvalid={Boolean(error)}
              value={condition.value ?? Number.NaN}
              onChange={(value) =>
                onUpdate((condition) => {
                  condition.value = value;
                })
              }
              placeholder="Enter a number..."
            />
          </HiddenFormLabel>
        ) : (
          <HiddenFormLabel label="Enter a value">
            <TextInput
              type={propertyType === ColumnType.BigInt ? "integer" : undefined}
              isDisabled={isDisabled}
              isInvalid={Boolean(error)}
              value={condition.value ?? ""}
              onChange={(event) =>
                onUpdate((condition) => {
                  condition.value = event.target.value;
                })
              }
            />
          </HiddenFormLabel>
        )}
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </Column>
      {conditionallyRenderPercentileCheckbox({
        allowPercentile,
        condition,
        onUpdate,
      })}
    </Row>
  );
};

/**
 * Walk a property's column reference down to its leaf and resolve a model
 * name from FilterableOptionsContext data. Returns null when no name can
 * be determined — callers fall back to a generic "model" label.
 *
 * This replaces a call to the heavy `useConditionSelect` hook (which was
 * previously used just to derive this same name via getSelectedColumn +
 * getModelName). The behavior matches the prior path for the cases that
 * `getModelName` actually returned a name for: Property (raw) columns and
 * trait columns. Other column shapes — synthetic, journey events, etc. —
 * already returned null in `getModelName`, so we do the same.
 */
function resolveModelName(
  property: PropertyCondition["property"],
  contextColumns: FilterableColumn[],
  traits: Array<{
    id: string | number;
    relationship?: { to_model?: { name: string } | null } | null;
  }>,
): string | null {
  if (typeof property !== "object" || property === null) return null;

  // Walk past Related/Transformed wrappers (the previous getSelectedColumn
  // matched on the inner column reference). Use ColumnReference so the
  // inner-column union (TraitColumn, JourneyEventColumn, …) is captured.
  let leaf: ColumnReference | null = property;
  while (
    leaf !== null &&
    (isRelatedColumn(leaf) || isTransformedColumn(leaf))
  ) {
    leaf = leaf.column;
  }
  if (leaf === null) return null;

  if (isTraitColumn(leaf)) {
    return (
      traits.find(
        (t) => t.id?.toString() === leaf.traitDefinitionId?.toString(),
      )?.relationship?.to_model?.name ?? null
    );
  }

  if (isRawColumn(leaf) && leaf.modelId != null) {
    const modelIdStr = leaf.modelId.toString();
    return (
      contextColumns.find((c) => c.model_id?.toString() === modelIdStr)
        ?.model_name ?? null
    );
  }

  return null;
}

const conditionallyRenderPercentileCheckbox = ({
  allowPercentile,
  condition,
  onUpdate,
}: {
  allowPercentile: boolean;
  condition: PropertyCondition;
  onUpdate: (updater: UpdaterEnforceDefined<PropertyCondition>) => void;
}) => {
  const propertyType = getPropertyType(condition);
  if (
    allowPercentile &&
    propertyType &&
    [ColumnType.Number, ColumnType.BigInt].includes(propertyType) &&
    PercentileOperators.includes(condition.operator)
  ) {
    return (
      <Box py={1.5}>
        <Tooltip
          message="Percentile filters use the entire parent model as the comparison group, not just the records
  that match the other filters."
        >
          <Checkbox
            label="Percentile"
            isChecked={Boolean(condition.propertyOptions?.percentile)}
            onChange={(event) =>
              onUpdate((condition) => {
                if (!condition.propertyOptions) {
                  condition.propertyOptions = {};
                }
                condition.propertyOptions.percentile = event.target.checked;
              })
            }
          />
        </Tooltip>
      </Box>
    );
  }

  return null;
};
